import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import axios from "./axios";
import toast from "react-hot-toast";

export const documentsKeys = {
  documentById: (id: number) => ["document", "id", id] as const,
  fleetDocuments: (
    fleetId: number,
    page: number,
    perPage: number,
    filters: TFleetDocumentsFilters
  ) =>
    [
      "fleetDocuments",
      "idFleet",
      fleetId,
      "page",
      page,
      "perPage",
      perPage,
      "filters",
      filters,
    ] as const,
  fleetDocumentsSummary: (fleetId: number) =>
    ["fleetDocumentsSummary", "idFleet", fleetId] as const,
  missingDocument: (fleetId: number, employeeId: number) =>
    ["missingDocument", "idFleet", fleetId, "employeeId", employeeId] as const,
  documentByEmployee: (idCompanie: number, idEmployeee: number) =>
    [
      "companie",
      idCompanie,
      "employee",
      "id",
      idEmployeee,
      "document",
    ] as const,
};

export enum typeOfDocument {
  contrat = "contract",
  permis_de_conduire = "license_card",
  carte_grise = "registration_certificate",
  carte_d_identite = "identity",
  certificat_d_assurance = "insurance_certificate",
}

interface RequestCreateDocument {
  name: string;
  vehicle_id?: number;
  user_id?: number;
  type: string;
  scan_id: string;
  number?: string;
  cost?: number;
  periodicity_id?: number;
}

export const useCreateDocument = (onSuccessSave?: () => void) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: async (createDocument: RequestCreateDocument) => {
      const data = await axios.post(`/strongbox/add`, createDocument);
      return data.data?.document;
    },
    onSuccess: (_) => {
      toast.success(t("common.save", "Change saved"));
      onSuccessSave && onSuccessSave();
    },
    onError: (err: any) => {
      toast.error(
        t(
          `errorDocument.${err?.response?.data?.message}`,
          String(err?.response?.data?.message || "Failed to save document")
        )
      );
    },
  });
};

export const useGetDocumentByVehicleId = (vehicleId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: documentsKeys.documentById(vehicleId),
    queryFn: async (): Promise<TDocument[]> => {
      const data = await axios.get(`/vehicle/${vehicleId}/documents`);
      return data.data?.documents;
    },
    enabled: !!vehicleId,
  });
  return { documents: data, error, ...res };
};

export const useReminderDocumentMissing = (
  fleetId: number,
  employeeId: number
) => {
  const { t } = useTranslation();
  return useMutation({
    mutationFn: () => {
      return axios.post(
        `/fleets/${fleetId}/employees/${employeeId}/send-reminder`
      );
    },
    onSuccess: (_) => {
      // "Envoyé", pas "Envoyer" : c'est un accusé de réception, pas un bouton.
      // La clé common.send reste réservée aux libellés de boutons (cf.
      // StepSearchCollaborator), d'où une clé common.sent distincte plutôt
      // qu'une retouche de la première.
      toast.success(t("common.sent", "Sent"));
    },
    onError: (err: any) => {
      toast.error(
        t(
          `${err?.response?.data?.message}`,
          String(err?.response?.data?.message)
        )
      );
    },
  });
};

export const useGetMissingDocumentEmployee = (
  fleetId: number,
  employeeId: number
) => {
  const { data, error, ...res } = useQuery({
    queryKey: documentsKeys.missingDocument(fleetId, employeeId),
    queryFn: async (): Promise<[]> => {
      const data = await axios.get(
        `/fleets/${fleetId}/employees/${employeeId}/missing-document`
      );
      return data.data;
    },
    enabled: !!fleetId && !!employeeId,
  });
  return { documents: data, error, ...res };
};

export const useGetDocumentByEmployee = (
  fleetId: number,
  employeeId: number
) => {
  const { data, error, ...res } = useQuery({
    queryKey: documentsKeys.documentByEmployee(fleetId, employeeId),
    queryFn: async (): Promise<TDocumentEmployee[]> => {
      const data = await axios.get(
        `/fleets/${fleetId}/employees/${employeeId}/documents`
      );
      return data.data?.documents;
    },
    enabled: !!fleetId && !!employeeId,
  });
  return { documents: data, error, ...res };
};

// ---------------------------------------------------------------------------
// GED — tous les documents de la flotte (véhicules + collaborateurs)
// Backend : GET /fleets/{fleetId}/documents (+ /summary)
// ---------------------------------------------------------------------------

export interface TFleetDocumentsFilters {
  search?: string;
  type?: string;
  document_type_id?: number;
  vehicle_id?: number;
  user_id?: number;
  source?: TFleetDocumentSource | "";
  start_date?: string;
  end_date?: string;
  sort?: "date_desc" | "date_asc" | "name_asc" | "name_desc";
}

interface ResponseGetFleetDocuments {
  data: TFleetDocument[];
  current_page: number;
  last_page: number;
  per_page: number;
  total: number;
}

/** Strips empty values so the query string only carries active filters. */
const cleanFilters = (filters: TFleetDocumentsFilters) =>
  Object.fromEntries(
    Object.entries(filters).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        String(value).trim().length > 0 &&
        value !== 0
    )
  );

export const useGetFleetDocuments = (
  fleetId: number,
  page: number = 1,
  perPage: number = 10,
  filters: TFleetDocumentsFilters = {}
) => {
  const { data, error, ...res } = useQuery({
    queryKey: documentsKeys.fleetDocuments(fleetId, page, perPage, filters),
    queryFn: async (): Promise<ResponseGetFleetDocuments> => {
      const response = await axios.get(`/fleets/${fleetId}/documents`, {
        params: {
          page,
          per_page: perPage,
          ...cleanFilters(filters),
        },
      });
      return response.data;
    },
    enabled: !!fleetId,
    staleTime: 0,
  });

  return {
    documents: data?.data ?? [],
    totalCount: data?.total ?? 0,
    currentPage: data?.current_page ?? 1,
    lastPage: data?.last_page ?? 1,
    error,
    ...res,
  };
};

export const useGetFleetDocumentsSummary = (fleetId: number) => {
  const { data, error, ...res } = useQuery({
    queryKey: documentsKeys.fleetDocumentsSummary(fleetId),
    queryFn: async (): Promise<TFleetDocumentsSummary> => {
      const response = await axios.get(`/fleets/${fleetId}/documents/summary`);
      return response.data;
    },
    enabled: !!fleetId,
    staleTime: 60 * 1000,
  });

  return { summary: data, error, ...res };
};
